import { UseFetchOptions, UseFetchReturn } from '@vueuse/core';
import {
    JsonApiDocument,
    JsonApiIdentifier,
    JsonApiResource,
    Model,
    ModelForType,
} from 'json-api-models';
import { cloneDeep, pick } from 'lodash-es';
import mitt from 'mitt';
import { computed, MaybeRef, ShallowRef, shallowRef, unref } from 'vue';

import { Models, models } from '../../models.ts';
import { useApiRequest } from './api.ts';
import { JSON_API_MEDIA_TYPE, useApiRequestBase } from './transport.ts';
import { markStarted, recordWrite } from './writes.ts';

export const JSON_API_EXT_ATOMIC = 'https://jsonapi.org/ext/atomic';

export const emitter = mitt<{
    created: { model: JsonApiResource };
    updated: {
        model: JsonApiResource;
        old?: Pick<JsonApiResource, 'attributes' | 'relationships' | 'meta'>;
    };
    deleted: { model: JsonApiIdentifier };
}>();

interface UseCreateRequestOptions extends UseFetchOptions {
    query?: string | URLSearchParams;
    meta?: JsonApiDocument['meta'];
}

export function useCreateRequest<T extends string>(
    type: T,
    data: MaybeRef<Omit<JsonApiResource<T>, 'type' | 'id'>> = {},
    options: UseCreateRequestOptions = {},
) {
    const { query, ...useFetchOptions } = options;
    const url = type + (query ? `?${query}` : '');
    const request = useApiRequest<ModelForType<T, Models>>(url, useFetchOptions);

    request.post(
        computed(() =>
            JSON.stringify({
                data: { type, ...unref(data) },
                meta: options.meta,
            }),
        ),
        JSON_API_MEDIA_TYPE,
    );

    request.onFetchResponse(() => {
        if (request.model.value) {
            emitter.emit('created', { model: request.model.value });
        }
    });

    return request;
}

interface UseUpdateRequestOptions extends UseCreateRequestOptions {
    optimistic?: boolean;
}

export function useUpdateRequest<T extends Model<JsonApiResource<any>, any>>(
    model: T,
    data: MaybeRef<Omit<JsonApiResource, 'type' | 'id'>> = {},
    options: UseUpdateRequestOptions = {},
) {
    const { query, optimistic = true, ...useFetchOptions } = options;

    const url = model.links?.self + (query ? `?${query}` : '');

    type Run = {
        write: ReturnType<typeof recordWrite>;
        oldModel: Pick<T, 'attributes' | 'relationships' | 'meta'>;
        sent: Omit<JsonApiResource, 'type' | 'id'>;
    };
    const runs = new WeakMap<object, Run>();

    const request = useApiRequest<T>(url, {
        ...useFetchOptions,
        beforeFetch: async (ctx) => {
            const oldModel = cloneDeep(pick(model, ['attributes', 'relationships', 'meta']));
            const sent = unref(data);

            if (optimistic) {
                model.merge(sent);
            }

            const write = recordWrite(model, sent, ctx);
            runs.set(ctx.options, { write, oldModel, sent });

            // Running the request again would abort this run, but once sent it
            // is most likely saved, so let it finish and settle normally. The
            // run replacing it then waits for it, keeping saves in order.
            delete ctx.options.signal;

            // A run that waited is sent after the earlier writes were saved, so
            // it starts now and takes its response's values for their fields.
            await write.earlier;
            markStarted(ctx);

            return useFetchOptions.beforeFetch?.(ctx) || ctx;
        },

        // Confirm the write before the response is synced or anything reacts
        // to the update, so requests started in response count as sent after
        // the server saved it. The response event only gets the response, so
        // the run is noted against it too.
        afterFetch: async (ctx) => {
            const run = runs.get(ctx.context.options);
            run?.write.confirm();
            if (run) runs.set(ctx.response, run);

            return useFetchOptions.afterFetch?.(ctx) || ctx;
        },

        // Roll back only the fields this run is still pending for, so a failure
        // doesn't discard later pending changes or a save already confirmed.
        // Fields the model didn't have are restored as not loaded, rather than
        // left with the unsaved value.
        onFetchError: async (ctx) => {
            const run = runs.get(ctx.context.options);

            if (run && optimistic) {
                const pending = run.write.pendingFields();
                const { attributes, relationships } = run.oldModel;

                model.merge({
                    attributes: Object.fromEntries(
                        pending.attributes.map((name) => [name, attributes[name]]),
                    ),
                    relationships: Object.fromEntries(
                        pending.relationships.map((name) => [
                            name,
                            { data: undefined, ...relationships?.[name] },
                        ]),
                    ),
                    meta: run.sent.meta && run.oldModel.meta,
                });
            }

            run?.write.abandon();

            return useFetchOptions.onFetchError?.(ctx) || ctx;
        },
    });

    request.patch(
        computed(() =>
            JSON.stringify({
                data: { ...model.identifier(), ...unref(data) },
                meta: options.meta,
            }),
        ),
        JSON_API_MEDIA_TYPE,
    );

    request.onFetchResponse((response) =>
        emitter.emit('updated', { model, old: runs.get(response)?.oldModel }),
    );

    return request;
}

export function useDeleteRequest<T extends JsonApiResource = JsonApiResource>(
    model: T,
    options: UseFetchOptions = {},
) {
    const request = useApiRequest(
        model.links?.self,
        { headers: { 'Content-Type': JSON_API_MEDIA_TYPE } },
        options,
    );
    request
        .delete()
        .text()
        .onFetchResponse(() => {
            emitter.emit('deleted', { model });
        });
    return request;
}

export type UseApiOperationsReturn = UseFetchReturn<JsonApiDocument> & {
    results: ShallowRef<Model[]>;
};

export type JsonApiOperation = {
    op: 'add' | 'update' | 'remove';
    ref?: JsonApiIdentifier;
    href?: string;
    data?: object;
    meta?: object;
    params?: object;
};

export function useApiOperations(
    operations: JsonApiOperation[],
    options: UseFetchOptions = {},
): UseApiOperationsReturn & PromiseLike<UseApiOperationsReturn> {
    const mediaType = `${JSON_API_MEDIA_TYPE}; ext="${JSON_API_EXT_ATOMIC}"`;
    const headers = { Accept: mediaType };

    const request = useApiRequestBase('operations', { headers }, options)
        .post(JSON.stringify({ 'atomic:operations': operations }), mediaType)
        .json();

    const results = shallowRef<Model[]>();

    request.onFetchResponse(() => {
        const data = request.data.value;
        if (!data) return;
        const syncedModels = data['atomic:results'].map((document: JsonApiDocument) =>
            document ? models.sync(document) : null,
        );
        results.value = syncedModels;

        operations.forEach((operation, i) => {
            if (operation.op === 'add') {
                emitter.emit('created', { model: syncedModels[i] });
            } else if (operation.op === 'update') {
                emitter.emit('updated', { model: syncedModels[i] });
            } else if (operation.op === 'remove' && operation.ref) {
                emitter.emit('deleted', { model: operation.ref });
            }
        });
    });

    const shell = {
        ...request,
        results,
        // oxlint-disable-next-line no-thenable
        then: undefined,
    } as UseApiOperationsReturn;

    return {
        ...shell,
        // oxlint-disable-next-line no-thenable
        then: (onFulfilled: any, onRejected: any) => {
            return request.then(
                () => onFulfilled(shell),
                (error) => onRejected(error),
            );
        },
    } as any;
}
